import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';
import lodashCompact from 'lodash/fp/compact';
import lodashSortBy from 'lodash/fp/sortBy';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import isFrontConversationNotAnswered from 'common/util/isFrontConversationNotAnswered';

import {
  getListingFullAddressString,
} from 'common/listingHelpers';

import {
  formatPhoneNumber,
  getFormattedFromNowDate,
} from 'common/formatters';

import {
  RENTLINX_LISTING_LEAD_STATUS_ARCHIVED,
} from 'common/rentlinxConstants';

import SrLink from 'client/components/SrLink/SrLink';
import SrCopyText from 'client/components/SrCopyText';
import SrBox from 'client/components/SrBox';

import './SrHouseholdKanbanBoardCardV2.less';

const sortByDate = lodashSortBy('_createdDate');

const getListingDetailsText = (listing) => {
  let label;

  if (listing._type === 'lead') {
    label = `Lead ${momentWithAustinTimezone(listing._createdDate).format('MMM D')}`;
  }

  if (listing._type === 'requested') {
    label = `Requested ${momentWithAustinTimezone(listing._createdDate).format('MMM D')}`;
  }

  if (listing._type === 'touring') {
    label = `Touring ${momentWithAustinTimezone(listing._createdDate).format('MMM D')}`;
  }

  return lodashCompact([
    label,
    listing.partnerDisplayName,
  ]).join(' | ');
};

const SrHouseholdKanbanBoardCardV2 = ({
  id,
  name,
  phone,
  email,
  note,
  frontConversation,
  frontLastMessageReceivedDate,
  listings,
  onClickNote,
  onClickFrontConversation,
  onClickUnarchive,
  onClickArchive,
}) => {
  const noteCreatedMoment = note ? moment(note) : null;
  const latestArchivedListing = listings.reduce((archivedListing, listing) => {
    if (
      listing.rentlinxArchivedDate
      && (
        !archivedListing
        || moment(archivedListing.rentlinxArchivedDate).isBefore(
          moment(listing.rentlinxArchivedDate)
        )
      )
    ) {
      return listing;
    }
    return archivedListing;
  }, null);

  const noteOverride = (
    latestArchivedListing
    && (
      !noteCreatedMoment
      || noteCreatedMoment.isBefore(latestArchivedListing.rentlinxArchivedDate)
    )
  )
    ? {
      note: latestArchivedListing.rentlinxPartnerArchiveReason,
      authorUserName: latestArchivedListing.archivedByUser?.name,
      createdDate: latestArchivedListing.rentlinxArchivedDate,
    }
    : note;

  return (
    <div className='SrHouseholdKanbanBoardCardV2' styleName='container'>
      <div styleName='header'>
        <span styleName='name'>
          {name || 'No name'}
          {frontConversation && (
            <span styleName={classnames(isFrontConversationNotAnswered(frontConversation) && 'warning')}>
              <span styleName='link chat' onClick={() => onClickFrontConversation(id)}>
                M
              </span>
            </span>
          )}
          <span styleName='link notes' onClick={() => onClickNote(id)}>
            N
          </span>
        </span>
        {frontLastMessageReceivedDate && (
          <span styleName='hour'>
            {getFormattedFromNowDate({
              date: frontLastMessageReceivedDate,
              compact: true,
            })}
          </span>
        )}
      </div>
      <div styleName='contactInfo'>
        {phone && (
          <span styleName='phone'>
            <SrCopyText
              className='kanbanCard'
              text={phone}
              formattedText={formatPhoneNumber(phone)}
              shouldShowTooltipOnHover={false}
            />
          </span>
        )}
        {phone && email && (
          <span>&nbsp;|&nbsp;</span>
        )}
        {email && (
          <span
            styleName={classnames('email', {
              truncate: email.length > 29,
            })}
          >
            <SrCopyText
              className='kanbanCard'
              text={email}
              shouldShowTooltipOnHover={false}
              shouldShowTooltipOnClick={email.length < 29}
            />
          </span>
        )}
      </div>
      {noteOverride && (
        <div styleName='note'>
          <span styleName='text'>{noteOverride.note}</span>
          <span styleName='details'>
            {[
              noteOverride.authorUserName,
              momentWithAustinTimezone(noteOverride.createdDate).format('MMM D, YYYY [at] h:mma'),
            ].join(' | ')}
          </span>
        </div>
      )}
      <div styleName='listings'>
        {sortByDate(listings).reverse().map((listing) => {
          const listingUrl = listing.partnerSlug
            ? `/partner/${listing.partnerSlug}/listing/${listing.id}`
            : `/listing/${listing.id}`;

          const rentlinxListingLeadIsArchived = (
            listing.rentlinxListingLeadStatus === RENTLINX_LISTING_LEAD_STATUS_ARCHIVED
          );

          return (
            <div
              styleName='listing'
              key={`${listing.id}-${listing._createdDate}`}
              style={rentlinxListingLeadIsArchived ? { opacity: '0.4' } : undefined}
            >
              {(onClickUnarchive || onClickArchive) && (
                <SrBox position='relative'>
                  <SrBox
                    width='16px'
                    height='16px'
                    backgroundImage={rentlinxListingLeadIsArchived
                      ? 'url(/images/unarchive-box-grey.svg)'
                      : 'url(/images/archive-box-v3-grey.svg)'
                    }
                    position='absolute'
                    right='-30px'
                    top='-34px'
                    cursor='pointer'
                    display='block'
                    onClick={() => rentlinxListingLeadIsArchived
                      ? onClickUnarchive && onClickUnarchive(listing.rentlinxListingLeadId)
                      : onClickArchive && onClickArchive(listing.rentlinxListingLeadId)
                    }
                  />
                </SrBox>
              )}
              <SrLink styleName='address' to={listingUrl} target='_blank'>
                {getListingFullAddressString(listing)}
              </SrLink>
              <span styleName='details'>
                {getListingDetailsText(listing)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

SrHouseholdKanbanBoardCardV2.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  note: PropTypes.object,
  frontConversation: PropTypes.object,
  frontLastMessageReceivedDate: PropTypes.string,
  listings: PropTypes.array.isRequired,
  onClickNote: PropTypes.func.isRequired,
  onClickFrontConversation: PropTypes.func,
  onClickUnarchive: PropTypes.func,
  onClickArchive: PropTypes.func,
};

SrHouseholdKanbanBoardCardV2.displayName = 'SrHouseholdKanbanBoardCardV2';

export default SrHouseholdKanbanBoardCardV2;
